/**
 * Frontend RBAC Utilities
 * 
 * This file provides client-side role and permission checking utilities
 * that mirror the backend RBAC system.
 */

// Define all available roles (must match backend)
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  TOUR_PROVIDER: 'tour_provider',
  SERVICE_PROVIDER: 'service_provider',
  VENDOR: 'vendor',
  VIEWER: 'viewer',
  PENDING: 'pending'
};

// Define vendor types (must match backend)
export const VENDOR_TYPES = {
  GAC: 'gac',
  SGS: 'sgs', 
  SAFE: 'safe',
  CUSTOMS: 'customs',
  IMMIGRATION: 'immigration',
  MARITIME: 'maritime',
  LOGISTICS: 'logistics',
  CATERING: 'catering',
  FUEL: 'fuel',
  WASTE: 'waste',
  GENERAL: 'general'
};

// Define all granular permissions (must match backend)
export const PERMISSIONS = {
  // User management
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  ASSIGN_ROLES: 'assign_roles',
  CREATE_USERS: 'create_users',
  DELETE_USERS: 'delete_users',
  
  // Bidding system
  CREATE_BIDS: 'create_bids',
  VIEW_BIDS: 'view_bids',
  VIEW_OWN_BIDS: 'view_own_bids',
  MANAGE_BIDDING_CYCLES: 'manage_bidding_cycles',
  APPROVE_BIDS: 'approve_bids',
  CLOSE_BIDDING: 'close_bidding',
  ASSIGN_VENDORS: 'assign_vendors',
  
  // Tours & RFP
  CREATE_TOURS: 'create_tours',
  EDIT_TOURS: 'edit_tours',
  VIEW_TOURS: 'view_tours',
  DELETE_TOURS: 'delete_tours',
  MANAGE_TOUR_ORDERS: 'manage_tour_orders',
  VIEW_RFP_ADMIN: 'view_rfp_admin',
  
  // Forms & Approvals
  CREATE_FORMS: 'create_forms',
  EDIT_FORMS: 'edit_forms',
  VIEW_FORMS: 'view_forms',
  APPROVE_FORMS: 'approve_forms',
  REJECT_FORMS: 'reject_forms',
  VIEW_OWN_FORMS: 'view_own_forms',
  
  // Calendar & Scheduling
  VIEW_CALENDAR: 'view_calendar',
  MANAGE_CALENDAR: 'manage_calendar',
  CREATE_CALLS: 'create_calls',
  EDIT_CALLS: 'edit_calls',
  
  // Vendor management
  MANAGE_VENDORS: 'manage_vendors',
  VIEW_VENDORS: 'view_vendors',
  APPROVE_VENDORS: 'approve_vendors',
  VIEW_PENDING_APPROVALS: 'view_pending_approvals',
  
  // Shore excursions
  MANAGE_SHOREX: 'manage_shorex',
  VIEW_SHOREX: 'view_shorex',
  CREATE_SHOREX: 'create_shorex',
  
  // Service confirmations
  MANAGE_SERVICE_CONFIRMATIONS: 'manage_service_confirmations',
  VIEW_SERVICE_CONFIRMATIONS: 'view_service_confirmations',
  
  // System administration
  SYSTEM_CONFIG: 'system_config',
  CACHE_CONTROL: 'cache_control',
  VIEW_LOGS: 'view_logs',
  BACKUP_SYSTEM: 'backup_system'
};

// Define role hierarchy (must match backend)
const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 5,
  [ROLES.ADMIN]: 4,
  [ROLES.MANAGER]: 3,
  [ROLES.SERVICE_PROVIDER]: 2,
  [ROLES.TOUR_PROVIDER]: 2,
  [ROLES.VENDOR]: 2,
  [ROLES.VIEWER]: 1,
  [ROLES.PENDING]: 0
};

// Define vendor-specific permissions (must match backend)
const VENDOR_TYPE_PERMISSIONS = {
  [VENDOR_TYPES.GAC]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_VENDORS,
    PERMISSIONS.VIEW_SHOREX,
    PERMISSIONS.VIEW_PENDING_APPROVALS
  ],
  
  [VENDOR_TYPES.SGS]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS, 
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.APPROVE_FORMS,
    PERMISSIONS.VIEW_PENDING_APPROVALS
  ],
  
  [VENDOR_TYPES.SAFE]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.APPROVE_FORMS,
    PERMISSIONS.VIEW_PENDING_APPROVALS
  ],
  
  [VENDOR_TYPES.CUSTOMS]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.APPROVE_FORMS
  ],
  
  [VENDOR_TYPES.IMMIGRATION]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.APPROVE_FORMS
  ],
  
  [VENDOR_TYPES.MARITIME]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_VENDORS
  ],
  
  [VENDOR_TYPES.LOGISTICS]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [VENDOR_TYPES.CATERING]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [VENDOR_TYPES.FUEL]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [VENDOR_TYPES.WASTE]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [VENDOR_TYPES.GENERAL]: [
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR
  ]
};

// Define permissions for each role (must match backend)
const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS),
  
  [ROLES.ADMIN]: [
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.ASSIGN_ROLES,
    PERMISSIONS.CREATE_USERS,
    PERMISSIONS.MANAGE_BIDDING_CYCLES,
    PERMISSIONS.APPROVE_BIDS,
    PERMISSIONS.VIEW_BIDS,
    PERMISSIONS.CLOSE_BIDDING,
    PERMISSIONS.ASSIGN_VENDORS,
    PERMISSIONS.CREATE_TOURS,
    PERMISSIONS.EDIT_TOURS,
    PERMISSIONS.VIEW_TOURS,
    PERMISSIONS.DELETE_TOURS,
    PERMISSIONS.MANAGE_TOUR_ORDERS,
    PERMISSIONS.VIEW_RFP_ADMIN,
    PERMISSIONS.APPROVE_FORMS,
    PERMISSIONS.REJECT_FORMS,
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.EDIT_FORMS,
    PERMISSIONS.MANAGE_CALENDAR,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.CREATE_CALLS,
    PERMISSIONS.EDIT_CALLS,
    PERMISSIONS.MANAGE_VENDORS,
    PERMISSIONS.VIEW_VENDORS,
    PERMISSIONS.APPROVE_VENDORS,
    PERMISSIONS.VIEW_PENDING_APPROVALS,
    PERMISSIONS.MANAGE_SHOREX,
    PERMISSIONS.VIEW_SHOREX,
    PERMISSIONS.CREATE_SHOREX,
    PERMISSIONS.MANAGE_SERVICE_CONFIRMATIONS,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_BIDS,
    PERMISSIONS.ASSIGN_VENDORS,
    PERMISSIONS.VIEW_TOURS,
    PERMISSIONS.EDIT_TOURS,
    PERMISSIONS.VIEW_RFP_ADMIN,
    PERMISSIONS.APPROVE_FORMS,
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.EDIT_CALLS,
    PERMISSIONS.VIEW_VENDORS,
    PERMISSIONS.VIEW_PENDING_APPROVALS,
    PERMISSIONS.VIEW_SHOREX,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [ROLES.TOUR_PROVIDER]: [
    PERMISSIONS.CREATE_BIDS,
    PERMISSIONS.VIEW_OWN_BIDS,
    PERMISSIONS.VIEW_TOURS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_OWN_FORMS
  ],
  
  [ROLES.SERVICE_PROVIDER]: [
    PERMISSIONS.CREATE_FORMS,
    PERMISSIONS.EDIT_FORMS,
    PERMISSIONS.VIEW_OWN_FORMS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_PENDING_APPROVALS,
    PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS
  ],
  
  [ROLES.VENDOR]: [
    // Base vendor permissions - specific permissions determined by vendor_type
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_OWN_FORMS
  ],
  
  [ROLES.VIEWER]: [
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_TOURS,
    PERMISSIONS.VIEW_OWN_FORMS
  ],
  
  [ROLES.PENDING]: []
};

// Legacy role mapping for backward compatibility
const LEGACY_ROLE_MAPPING = {
  'Admin': ROLES.ADMIN,
  'Tour Provider': ROLES.TOUR_PROVIDER,
  'Service Provider': ROLES.SERVICE_PROVIDER,
  'GAC': ROLES.VENDOR,
  'Viewer': ROLES.VIEWER,
  'New': ROLES.PENDING,
  'PAD': ROLES.MANAGER,
  'SHOREX': ROLES.MANAGER,
  'SGS': ROLES.VENDOR,
  'SAFE': ROLES.VENDOR,
  'RFPAdmin': ROLES.ADMIN
};

// Legacy vendor type mapping for backward compatibility
const LEGACY_VENDOR_TYPE_MAPPING = {
  'GAC': VENDOR_TYPES.GAC,
  'SGS': VENDOR_TYPES.SGS,
  'SAFE': VENDOR_TYPES.SAFE
};

/**
 * Convert legacy roles to new role format
 * @param {Array} legacyRoles - Array of legacy role strings
 * @returns {Array} Array of new role strings
 */
export function convertLegacyRoles(legacyRoles) {
  if (!legacyRoles || legacyRoles.length === 0) return [ROLES.PENDING];
  
  return legacyRoles.map(role => 
    LEGACY_ROLE_MAPPING[role] || role.toLowerCase()
  ).filter(role => Object.values(ROLES).includes(role));
}

/**
 * Get vendor type from user roles
 * @param {Array} userRoles - Array of user's roles
 * @returns {string|null} Vendor type or null
 */
export function getVendorType(userRoles) {
  if (!userRoles || userRoles.length === 0) return null;
  
  // Check for legacy vendor type mapping
  const legacyVendorType = userRoles.find(role => LEGACY_VENDOR_TYPE_MAPPING[role]);
  if (legacyVendorType) {
    return LEGACY_VENDOR_TYPE_MAPPING[legacyVendorType];
  }
  
  // Check for direct vendor type
  const directVendorType = userRoles.find(role => Object.values(VENDOR_TYPES).includes(role));
  if (directVendorType) {
    return directVendorType;
  }
  
  return null;
}

/**
 * Get vendor-specific permissions
 * @param {Array} userRoles - Array of user's roles
 * @returns {Array} Array of vendor-specific permissions
 */
export function getVendorPermissions(userRoles) {
  const vendorType = getVendorType(userRoles);
  if (!vendorType) return [];
  
  return VENDOR_TYPE_PERMISSIONS[vendorType] || [];
}

/**
 * Check if a user has a specific permission
 * @param {Array|Object} userRolesOrUser - Array of user's roles OR user object with permissions
 * @param {string} permission - Permission to check
 * @param {Object} context - Additional context (e.g., resource ownership, vendor type)
 * @returns {boolean}
 */
export function hasPermission(userRolesOrUser, permission, context = {}) {
  // Handle new format where user object with permissions is passed
  if (userRolesOrUser && typeof userRolesOrUser === 'object' && !Array.isArray(userRolesOrUser)) {
    const user = userRolesOrUser;
    
    // If user has permissions array from backend, use it directly
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }
    
    // Fallback to using roles if permissions not available
    const userRoles = user.rules || [];
    return hasPermissionFromRoles(userRoles, permission, context);
  }
  
  // Handle legacy format where only roles array is passed
  const userRoles = userRolesOrUser;
  return hasPermissionFromRoles(userRoles, permission, context);
}

/**
 * Check permission using role-based logic (fallback method)
 * @param {Array} userRoles - Array of user's roles
 * @param {string} permission - Permission to check
 * @param {Object} context - Additional context
 * @returns {boolean}
 */
function hasPermissionFromRoles(userRoles, permission, context = {}) {
  if (!userRoles || userRoles.length === 0) return false;
  
  // Normalize legacy roles
  const normalizedRoles = convertLegacyRoles(userRoles);
  
  // Check if any of the user's roles has the required permission
  for (const role of normalizedRoles) {
    const rolePermissions = ROLE_PERMISSIONS[role] || [];
    
    if (rolePermissions.includes(permission)) {
      return true;
    }
    
    // Handle vendor-specific permissions
    if (role === ROLES.VENDOR && context.vendorType) {
      const vendorPermissions = VENDOR_TYPE_PERMISSIONS[context.vendorType] || [];
      if (vendorPermissions.includes(permission)) {
        return true;
      }
    }
    
    // Handle legacy vendor type from user roles (for backward compatibility)
    if (role === ROLES.VENDOR && !context.vendorType) {
      // Try to determine vendor type from original roles
      const legacyVendorType = userRoles.find(r => LEGACY_VENDOR_TYPE_MAPPING[r]);
      if (legacyVendorType) {
        const vendorType = LEGACY_VENDOR_TYPE_MAPPING[legacyVendorType];
        const vendorPermissions = VENDOR_TYPE_PERMISSIONS[vendorType] || [];
        if (vendorPermissions.includes(permission)) {
          return true;
        }
      }
    }
    
    // Handle context-specific permissions
    if (context.isOwner && permission.includes('_OWN_')) {
      const basePermission = permission.replace('_OWN_', '_');
      if (rolePermissions.includes(basePermission)) {
        return true;
      }
      
      // Check vendor permissions for ownership-based access
      if (role === ROLES.VENDOR) {
        const vendorType = context.vendorType || 
          LEGACY_VENDOR_TYPE_MAPPING[userRoles.find(r => LEGACY_VENDOR_TYPE_MAPPING[r])];
        if (vendorType) {
          const vendorPermissions = VENDOR_TYPE_PERMISSIONS[vendorType] || [];
          if (vendorPermissions.includes(basePermission)) {
            return true;
          }
        }
      }
    }
  }
  
  return false;
}

/**
 * Check if a user has any of the specified permissions
 * @param {Array} userRoles - Array of user's roles
 * @param {Array} permissions - Array of permissions to check
 * @param {Object} context - Additional context
 * @returns {boolean}
 */
export function hasAnyPermission(userRoles, permissions, context = {}) {
  return permissions.some(permission => 
    hasPermission(userRoles, permission, context)
  );
}

/**
 * Check if a user has all of the specified permissions
 * @param {Array} userRoles - Array of user's roles
 * @param {Array} permissions - Array of permissions to check
 * @param {Object} context - Additional context
 * @returns {boolean}
 */
export function hasAllPermissions(userRoles, permissions, context = {}) {
  return permissions.every(permission => 
    hasPermission(userRoles, permission, context)
  );
}

/**
 * Check if a user has a specific role
 * @param {Array} userRoles - Array of user's roles
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export function hasRole(userRoles, role) {
  if (!userRoles || userRoles.length === 0) return false;
  
  const normalizedRoles = convertLegacyRoles(userRoles);
  return normalizedRoles.includes(role) || userRoles.includes(role);
}

/**
 * Check if a user has any of the specified roles
 * @param {Array} userRoles - Array of user's roles
 * @param {Array} roles - Array of roles to check
 * @returns {boolean}
 */
export function hasAnyRole(userRoles, roles) {
  return roles.some(role => hasRole(userRoles, role));
}

/**
 * Get all permissions for a user's roles
 * @param {Array} userRoles - Array of user's roles
 * @returns {Array} Array of permissions
 */
export function getUserPermissions(userRoles) {
  if (!userRoles || userRoles.length === 0) return [];
  
  const normalizedRoles = convertLegacyRoles(userRoles);
  const permissions = new Set();
  
  for (const role of normalizedRoles) {
    const rolePermissions = ROLE_PERMISSIONS[role] || [];
    rolePermissions.forEach(permission => permissions.add(permission));
  }
  
  return Array.from(permissions);
}

/**
 * Get user's highest role level
 * @param {Array} userRoles - Array of user's roles
 * @returns {number} Highest role level
 */
export function getUserLevel(userRoles) {
  if (!userRoles || userRoles.length === 0) return 0;
  
  const normalizedRoles = convertLegacyRoles(userRoles);
  
  return Math.max(...normalizedRoles.map(role => 
    ROLE_HIERARCHY[role] || 0
  ));
}

/**
 * Check if a user is pending approval
 * @param {Array} userRoles - Array of user's roles
 * @returns {boolean}
 */
export function isPendingUser(userRoles) {
  return hasRole(userRoles, ROLES.PENDING) || 
         (userRoles && userRoles.includes('New'));
}

/**
 * Check if user is a vendor
 * @param {Array} userRoles - Array of user's roles
 * @returns {boolean}
 */
export function isVendor(userRoles) {
  if (!userRoles || userRoles.length === 0) return false;
  
  const normalizedRoles = convertLegacyRoles(userRoles);
  return normalizedRoles.includes(ROLES.VENDOR) || getVendorType(userRoles) !== null;
}

/**
 * Get display name for vendor type
 * @param {string} vendorType - Vendor type
 * @returns {string} Display name
 */
export function getVendorTypeDisplayName(vendorType) {
  const vendorTypeNames = {
    [VENDOR_TYPES.GAC]: 'GAC (General Authority of Customs)',
    [VENDOR_TYPES.SGS]: 'SGS (Société Générale de Surveillance)',
    [VENDOR_TYPES.SAFE]: 'SAFE (Safety & Security)',
    [VENDOR_TYPES.CUSTOMS]: 'Customs Authority',
    [VENDOR_TYPES.IMMIGRATION]: 'Immigration Services',
    [VENDOR_TYPES.MARITIME]: 'Maritime Authority',
    [VENDOR_TYPES.LOGISTICS]: 'Logistics Provider',
    [VENDOR_TYPES.CATERING]: 'Catering Services',
    [VENDOR_TYPES.FUEL]: 'Fuel Services',
    [VENDOR_TYPES.WASTE]: 'Waste Management',
    [VENDOR_TYPES.GENERAL]: 'General Vendor'
  };
  
  return vendorTypeNames[vendorType] || vendorType.charAt(0).toUpperCase() + vendorType.slice(1);
}

/**
 * Check if a user is an admin (any level)
 * @param {Array} userRoles - Array of user's roles
 * @returns {boolean}
 */
export function isAdmin(userRoles) {
  return hasAnyRole(userRoles, [ROLES.SUPER_ADMIN, ROLES.ADMIN]);
}

/**
 * Check if a user can manage other users
 * @param {Array} userRoles - Array of user's roles
 * @returns {boolean}
 */
export function canManageUsers(userRoles) {
  return hasPermission(userRoles, PERMISSIONS.MANAGE_USERS);
}

/**
 * Check if a user can assign roles
 * @param {Array} userRoles - Array of user's roles
 * @returns {boolean}
 */
export function canAssignRoles(userRoles) {
  return hasPermission(userRoles, PERMISSIONS.ASSIGN_ROLES);
}

/**
 * Get roles that a user can assign to others
 * @param {Array} assignerRoles - Roles of the user trying to assign
 * @returns {Array} Array of assignable roles
 */
export function getAssignableRoles(assignerRoles) {
  if (!canAssignRoles(assignerRoles)) return [];
  
  const assignerLevel = getUserLevel(assignerRoles);
  
  return Object.entries(ROLES)
    .filter(([_, role]) => {
      const roleLevel = ROLE_HIERARCHY[role] || 0;
      return assignerLevel > roleLevel || hasRole(assignerRoles, ROLES.SUPER_ADMIN);
    })
    .map(([_, role]) => role);
}

/**
 * Get a human-readable role name
 * @param {string} role - Role constant
 * @returns {string} Human-readable role name
 */
export function getRoleDisplayName(role) {
  const roleNames = {
    [ROLES.SUPER_ADMIN]: 'Super Administrator',
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.TOUR_PROVIDER]: 'Tour Provider',
    [ROLES.SERVICE_PROVIDER]: 'Service Provider',
    [ROLES.GAC_VENDOR]: 'GAC Vendor',
    [ROLES.VIEWER]: 'Viewer',
    [ROLES.PENDING]: 'Pending Approval'
  };
  
  return roleNames[role] || role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ');
}

/**
 * Get a human-readable permission name
 * @param {string} permission - Permission constant
 * @returns {string} Human-readable permission name
 */
export function getPermissionDisplayName(permission) {
  return permission
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Filter navigation items based on user permissions
 * @param {Array} navItems - Array of navigation items with required permissions
 * @param {Array} userRoles - Array of user's roles
 * @returns {Array} Filtered navigation items
 */
export function filterNavItems(navItems, userRoles) {
  if (!navItems) return [];
  
  return navItems.filter(item => {
    if (!item.requiredPermissions && !item.requiredRoles) return true;
    
    if (item.requiredPermissions) {
      return hasAnyPermission(userRoles, item.requiredPermissions);
    }
    
    if (item.requiredRoles) {
      return hasAnyRole(userRoles, item.requiredRoles);
    }
    
    return false;
  });
}
